import { UnstyledButton } from "@mantine/core";
import { forwardRef } from "react";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";

import { Text } from "../Typographies/Text";
import classes from "./Toggle.module.css";

type ToggleShape = "rounded" | "square";
type ToggleItemProps = {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  selected: boolean;
  disabled?: boolean;
  shape: ToggleShape;
  onClick?: () => void;
  showBg?: boolean;
};

const SHAPE_CONFIG: Record<ToggleShape, { itemRadius: string; containerRadius: string }> = {
  rounded: { itemRadius: radius.lg, containerRadius: radius.xxl },
  square: { itemRadius: radius.sm, containerRadius: radius.md },
};

const ToggleItem = forwardRef<HTMLButtonElement, ToggleItemProps>(function ToggleItem(
  { label, icon, selected, disabled, shape, onClick, showBg = true },
  ref,
) {
  const { itemRadius } = SHAPE_CONFIG[shape];
  const textColor = disabled
    ? colors.text.primaryDisabled
    : selected
      ? colors.text.primaryDefault
      : colors.text.secondaryDefault;
  const iconColor = disabled
    ? colors.icon.primaryDisabled
    : selected
      ? colors.icon.primaryDefault
      : colors.icon.secondary;

  return (
    <UnstyledButton
      ref={ref}
      disabled={disabled}
      onClick={onClick}
      data-selected={selected || undefined}
      data-no-hover-bg={!showBg || undefined}
      className={classes.item}
      style={
        {
          "--toggle-item-hover-bg": colors.bg.button.custom.hover,
          "--toggle-item-hover-color": colors.text.primaryDefault,
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: spacing[1.5],
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
          paddingTop: spacing[2],
          paddingBottom: spacing[2],
          borderRadius: itemRadius,
          backgroundColor:
            disabled && showBg
              ? colors.bg.button.custom.default
              : showBg && selected
                ? colors.bg.elevation.primary
                : "transparent",
          boxShadow: showBg && selected && !disabled ? shadows.xs : "none",
          color: iconColor,
          overflow: "hidden",
          flexShrink: 0,
          whiteSpace: "nowrap",
        } as React.CSSProperties
      }
    >
      {icon}
      {label !== undefined && (
        <Text size="small" type="medium" color={textColor}>
          {label}
        </Text>
      )}
    </UnstyledButton>
  );
});

export { ToggleItem, SHAPE_CONFIG };
export type { ToggleShape };
